import { LinkedInWizardData } from "@/pages/LinkedInWizard";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { Sparkles, HelpCircle, Target, Users, Briefcase } from "lucide-react";

interface LinkedInWizardStep11Props {
  data: LinkedInWizardData;
  updateData: (updates: Partial<LinkedInWizardData>) => void;
  onNext: () => void;
  onPrev: () => void;
  onGenerate: () => void;
}

const CTA_OPTIONS = [
  {
    value: "book-call",
    label: "Book a free call",
    description: "Direct them to schedule a consultation or discovery call"
  },
  {
    value: "visit-website",
    label: "Visit website / portfolio",
    description: "Send them to your website, portfolio, or landing page"
  },
  {
    value: "send-dm",
    label: "Send a DM to learn more",
    description: "Encourage direct messaging for further conversation"
  },
  {
    value: "hire-me",
    label: "Hire me / Get in touch",
    description: "General invitation to reach out for opportunities"
  },
  {
    value: "apply-job",
    label: "Apply to a job or opportunity",
    description: "If you're actively job seeking"
  },
  {
    value: "download-resource",
    label: "Download a free resource",
    description: "Lead magnet like an ebook, template, or guide"
  },
  {
    value: "follow-updates",
    label: "Follow me for updates",
    description: "Build your network and thought leadership"
  },
  {
    value: "view-case-study",
    label: "View featured case study or project",
    description: "Showcase your work and results"
  },
  {
    value: "not-sure",
    label: "I'm not sure - help me choose",
    description: "Get personalized recommendations based on your goals and industry"
  }
];

export function LinkedInWizardStep11({ data, updateData, onGenerate }: LinkedInWizardStep11Props) {
  // Get recommendation based on user's goals and industry
  const getRecommendation = () => {
    const goals = data.primaryGoals || [];
    const industries = data.industries || [];
    const userCategories = data.userCategories || [];
    
    // Job seeker
    if (userCategories.includes('Job Seeker') || goals.includes('Get hired for a new job')) {
      return 'apply-job';
    }
    
    // Entrepreneur/Business owner
    if (userCategories.includes('Entrepreneur') || userCategories.includes('Business Owner')) {
      return 'book-call';
    }
    
    // Consultant/Freelancer
    if (userCategories.includes('Consultant') || userCategories.includes('Freelancer')) {
      return 'send-dm';
    }
    
    // Default for thought leadership
    if (goals.includes('Be seen as a thought leader')) {
      return 'follow-updates';
    }
    
    // Default recommendation
    return 'hire-me';
  };

  const recommendedOption = getRecommendation();
  const isRecommended = (value: string) => value === recommendedOption;

  return (
    <div className="space-y-8">
      {/* Step Number and Header */}
      <div className="relative">
        <div className="absolute -top-2 -left-2">
          <div className="w-12 h-12 bg-linkedin rounded-full flex items-center justify-center shadow-linkedin">
            <span className="text-xl font-bold text-white">11</span>
          </div>
        </div>
        <div className="ml-12 text-center">
          <h2 className="text-2xl font-bold mb-2 text-linkedin">What's Your Call to Action?</h2>
          <p className="text-linkedin/70 mb-6">
            How do you want people to connect with you after reading your LinkedIn profile?
          </p>
        </div>
      </div>

      <div className="space-y-6">
        <Label className="text-lg font-semibold block text-linkedin">
          Choose your preferred call to action: *
        </Label>
        
        <RadioGroup
          value={data.callToAction || ""}
          onValueChange={(value) => updateData({ callToAction: value })}
          className="space-y-3"
        >
          {CTA_OPTIONS.map((option) => {
            const isSelected = data.callToAction === option.value;
            const isRecommendedOption = isRecommended(option.value);
            
            return (
              <Card 
                key={option.value} 
                className={`p-4 relative transition-all border-linkedin/20 ${
                  isSelected 
                        ? 'border-green-500 bg-green-50 dark:bg-green-950 ring-2 ring-green-200 shadow-md' 
                    : 'border-linkedin/30 bg-linkedin-light/10 hover:border-linkedin/50 hover:bg-linkedin-light/20'
                } ${option.value === 'not-sure' ? 'border-2 border-dashed border-linkedin/50 bg-linkedin-light/20' : ''}`}
              >
                {isRecommendedOption && option.value !== 'not-sure' && (
                  <div className="absolute -top-2 -right-2 bg-linkedin text-white px-3 py-1 text-xs font-bold rounded-full shadow-lg border-2 border-white">
                    <Target className="w-3 h-3 mr-1 inline" />
                    Recommended
                  </div>
                )}
                <div className="flex items-start space-x-3">
                  <RadioGroupItem 
                    value={option.value} 
                    id={option.value}
                    className={`mt-1 ${
                      isSelected 
                        ? 'border-green-500 text-green-600' 
                        : 'border-linkedin text-linkedin'
                    }`}
                  />
                  <div className="flex-1">
                    <label 
                      htmlFor={option.value}
                      className={`font-medium cursor-pointer block flex items-center gap-2 ${
                        isSelected ? 'text-green-800 dark:text-green-200' : 'text-linkedin dark:text-linkedin'
                      } ${option.value === 'not-sure' ? 'text-linkedin dark:text-linkedin' : ''}`}
                    >
                      {option.value === 'not-sure' && <HelpCircle className="w-4 h-4" />}
                      {option.label}
                    </label>
                    <p className={`text-sm mt-1 ${
                      isSelected ? 'text-green-700 dark:text-green-300' : 'text-muted-foreground'
                    } ${option.value === 'not-sure' ? 'text-linkedin/70' : ''}`}>
                      {option.description}
                    </p>
                  </div>
                </div>
              </Card>
            );
          })}
        </RadioGroup>
      </div>

      {data.callToAction && data.callToAction !== 'not-sure' && (
        <Card className="p-4 bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800">
          <h3 className="font-semibold text-green-800 dark:text-green-200 mb-2">
            Your Call to Action:
          </h3>
          <p className="text-green-700 dark:text-green-300">
            {CTA_OPTIONS.find(o => o.value === data.callToAction)?.label}
          </p>
        </Card>
      )}

      {/* Recommendations for "not sure" */}
      {data.callToAction === 'not-sure' && (
        <Card className="p-6 bg-linkedin-light border-linkedin/20">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Target className="w-6 h-6 text-linkedin" />
              <h3 className="text-lg font-semibold text-linkedin">
                Our Recommendation Based on Your Profile
              </h3>
            </div>
            <div className="bg-white rounded-lg p-4 border border-linkedin/20">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-linkedin text-white px-3 py-1 rounded-full text-sm font-bold">
                  Best Match
                </div>
                <span className="font-medium text-linkedin">
                  {CTA_OPTIONS.find(o => o.value === recommendedOption)?.label}
                </span>
              </div>
              <p className="text-sm text-linkedin/70">
                {CTA_OPTIONS.find(o => o.value === recommendedOption)?.description}
              </p>
              <Button 
                onClick={() => updateData({ callToAction: recommendedOption })}
                className="mt-3 bg-linkedin hover:bg-linkedin-hover text-white"
                size="sm"
              >
                Use This Recommendation
              </Button>
            </div>
          </div>
        </Card>
      )}

      <div className="flex justify-center pt-6">
        <Button 
          onClick={onGenerate}
          size="lg"
          className="px-8 py-3 text-lg bg-linkedin hover:bg-linkedin-hover text-linkedin-foreground shadow-linkedin"
          disabled={!data.callToAction || data.callToAction === 'not-sure'}
        >
          <Sparkles className="w-5 h-5 mr-2" />
          Generate My LinkedIn Profile
        </Button>
      </div>
    </div>
  );
}